/**
 * NextPlayGuideHelper - Helper for managing music autoplay
 * 
 * Features:
 * - Supports multiple guide types (genre, category, playlist, artist)
 * - Tracks playback history in localStorage to avoid repeats
 * - Auto-resets history when all items in a group have been played
 */

// ============================================================================
// CONSTANTS
// ============================================================================

var HISTORY_KEY_PREFIX = "next_play_guide_history_";

var GuideType = {
    GENRE: "genre",
    CATEGORY: "category",
    PLAYLIST: "playlist",
    ARTIST: "artist",
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create a NextPlayGuide entity from content data
 * Falls back to category-based guide if not specified
 * @param {Object} contentData - Player resource data
 * @returns {Object} - NextPlayGuide entity with type, slug, and name
 */
function getNextPlayGuide(contentData) {
    if (!contentData) {
        return { type: GuideType.CATEGORY, slug: "", name: "" };
    }

    // If nextPlayGuide is specified in the API response, use it
    if (contentData.nextPlayGuide) {
        var guide = contentData.nextPlayGuide;
        return {
            type: _parseGuideType(guide.type),
            slug: guide.slug || "",
            name: guide.name || "",
        };
    }

    // Fallback: create category-based guide from first category
    var categorySlug = contentData.categoryKeys && contentData.categoryKeys[0];
    if (categorySlug) {
        console.log("[NextPlayGuide] Using category fallback:", categorySlug);
        return {
            type: GuideType.CATEGORY,
            slug: categorySlug,
            name: "",
        };
    }

    // If no category, try genre
    var genreSlug = contentData.genreKeys && contentData.genreKeys[0];
    if (genreSlug) {
        console.log("[NextPlayGuide] Using genre fallback:", genreSlug);
        return {
            type: GuideType.GENRE,
            slug: genreSlug,
            name: "",
        };
    }

    console.warn("[NextPlayGuide] No guide available");
    return { type: GuideType.CATEGORY, slug: "", name: "" };
}

/**
 * Parse guide type from API value
 */
function _parseGuideType(typeValue) {
    if (!typeValue) return GuideType.CATEGORY;

    var type = String(typeValue).toLowerCase();
    switch (type) {
        case "genre":
            return GuideType.GENRE;
        case "category":
            return GuideType.CATEGORY;
        case "playlist":
            return GuideType.PLAYLIST;
        case "artist":
            return GuideType.ARTIST;
        default:
            return GuideType.CATEGORY;
    }
}

/**
 * Build API query parameters for fetching next music content
 * @param {Object} guide - NextPlayGuide entity
 * @returns {Object} - Query parameters for content API
 */
function buildQueryParameters(guide) {
    var params = {
        kind: "music",
        sortBinName: "createdAt",
        sortType: "desc",
        lazyLoading: true,
        pageIndex: 20,
        pageNumber: 1,
    };

    if (!guide || !guide.slug) return params;

    switch (guide.type) {
        case GuideType.GENRE:
            params.genre = guide.slug;
            break;
        case GuideType.CATEGORY:
            params.category = guide.slug;
            break;
        case GuideType.PLAYLIST:
            params.playlist = guide.slug;
            break;
        case GuideType.ARTIST:
            params.artist = guide.name || guide.slug;
            break;
        default:
            params.category = guide.slug;
    }

    console.log("[NextPlayGuide] Query params:", params);
    return params;
}

// ============================================================================
// PLAYBACK HISTORY MANAGEMENT
// ============================================================================

/**
 * Get storage key for a guide's history
 */
function _getHistoryKey(guide) {
    return HISTORY_KEY_PREFIX + guide.type + "_" + guide.slug;
}

/**
 * Get playback history for a specific guide
 * @param {Object} guide - NextPlayGuide entity
 * @returns {Array} - Array of played slugs
 */
function getPlaybackHistory(guide) {
    try {
        var key = _getHistoryKey(guide);
        var historyJson = localStorage.getItem(key);
        if (!historyJson) return [];
        return JSON.parse(historyJson);
    } catch (e) {
        console.warn("[NextPlayGuide] Error reading history:", e);
        return [];
    }
}

/**
 * Save playback history for a specific guide
 * @param {Object} guide - NextPlayGuide entity
 * @param {Array} slugs - Array of played slugs
 */
function savePlaybackHistory(guide, slugs) {
    try {
        var key = _getHistoryKey(guide);
        localStorage.setItem(key, JSON.stringify(slugs));
    } catch (e) {
        console.warn("[NextPlayGuide] Error saving history:", e);
    }
}

/**
 * Clear playback history for a specific guide
 */
function clearPlaybackHistory(guide) {
    try {
        var key = _getHistoryKey(guide);
        localStorage.removeItem(key);
        console.log("[NextPlayGuide] Cleared history for:", guide.type, guide.slug);
    } catch (e) {
        console.warn("[NextPlayGuide] Error clearing history:", e);
    }
}

/**
 * Mark an item as played in the history
 * @param {string} slug - Content slug
 * @param {Object} guide - NextPlayGuide entity
 */
function markAsPlayed(slug, guide) {
    if (!slug || !guide) return;

    var playedSlugs = getPlaybackHistory(guide);
    if (playedSlugs.indexOf(slug) === -1) {
        playedSlugs.push(slug);
        savePlaybackHistory(guide, playedSlugs);
        console.log("[NextPlayGuide] Marked as played:", slug, "Total:", playedSlugs.length);
    }
}

// ============================================================================
// FILTERING AND SELECTION
// ============================================================================

/**
 * Filter out current item and already played items from results
 * Auto-resets history if no unplayed items remain
 * @param {Array} musicItems - Array of music content items
 * @param {string} currentSlug - Current playing item slug
 * @param {Object} guide - NextPlayGuide entity
 * @returns {Object} - { items: [], historyWasReset: boolean }
 */
function filterPlayedItems(musicItems, currentSlug, guide) {
    if (!musicItems || !Array.isArray(musicItems)) {
        return { items: [], historyWasReset: false };
    }

    console.log("[NextPlayGuide] Filtering", musicItems.length, "items (current:", currentSlug, ")");

    var playedSlugs = getPlaybackHistory(guide);
    console.log("[NextPlayGuide] Previously played:", playedSlugs.length, "items");

    // Filter out current item and played items
    var filtered = musicItems.filter(function (item) {
        var slug = item.slug;
        if (!slug) return false;
        if (slug === currentSlug) return false;
        return playedSlugs.indexOf(slug) === -1;
    });

    console.log("[NextPlayGuide] After filtering:", filtered.length, "items remaining");

    // If no items left after filtering, reset history and try again
    if (filtered.length === 0 && musicItems.length > 0) {
        console.log("[NextPlayGuide] No unplayed items left, resetting history");
        clearPlaybackHistory(guide);

        // Re-filter with empty history
        var resetFiltered = musicItems.filter(function (item) {
            return item.slug && item.slug !== currentSlug;
        });

        return {
            items: resetFiltered,
            historyWasReset: true,
        };
    }

    return {
        items: filtered,
        historyWasReset: false,
    };
}

/**
 * Select the next music item from filtered list
 * Returns first item (most recent based on sort)
 * @param {Array} filteredItems - Array of filtered music items
 * @returns {Object|null} - Next music item or null
 */
function selectNextMusic(filteredItems) {
    if (!filteredItems || filteredItems.length === 0) {
        console.log("[NextPlayGuide] No music items available");
        return null;
    }

    var nextItem = filteredItems[0];
    console.log("[NextPlayGuide] Selected next music:", nextItem.slug);

    return nextItem;
}

// ============================================================================
// EXPORTS
// ============================================================================

var NextPlayGuideHelper = {
    GuideType: GuideType,
    getNextPlayGuide: getNextPlayGuide,
    buildQueryParameters: buildQueryParameters,
    getPlaybackHistory: getPlaybackHistory,
    markAsPlayed: markAsPlayed,
    clearPlaybackHistory: clearPlaybackHistory,
    filterPlayedItems: filterPlayedItems,
    selectNextMusic: selectNextMusic,
};

export default NextPlayGuideHelper;
export {
    GuideType,
    getNextPlayGuide,
    buildQueryParameters,
    getPlaybackHistory,
    markAsPlayed,
    clearPlaybackHistory,
    filterPlayedItems,
    selectNextMusic,
};
